import { useState, type ChangeEvent } from "react";
import { Save, UserRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { MX_TIMEZONES, allTimezones, type UserSettings } from "@/lib/settings";
import { Field, SettingsSection } from "./SettingsSection";
import { EmailChangeDialog } from "./EmailChangeDialog";

const OTHERS = allTimezones();

export function ProfileCard({ data, onSaved }: { data: UserSettings; onSaved: (d: UserSettings) => void }) {
  const { refresh } = useAuth();
  const [name, setName] = useState(data.name);
  const [tz, setTz] = useState(data.timezone);
  const [saving, setSaving] = useState(false);
  const dirty = name.trim() !== data.name || tz !== data.timezone;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch<UserSettings>("/settings/profile", { name: name.trim(), timezone: tz });
      onSaved(r.data);
      await refresh();
      toast.success("Perfil actualizado");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  return (
    <SettingsSection id="perfil" icon={UserRound} title="Perfil" desc="Tu nombre y la zona horaria que usa la app para fechas, avisos y cortes del mes.">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Nombre" htmlFor="settings-name">
          <Input id="settings-name" value={name} onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)} maxLength={80} className="h-11" data-testid="settings-name-input" />
        </Field>
        <Field label="Correo" htmlFor="settings-email" hint="Para cambiarlo te mandamos un enlace de confirmación al correo nuevo.">
          <Input id="settings-email" value={data.email} readOnly disabled className="h-11 bg-slate-50" data-testid="settings-email-input" />
          <EmailChangeDialog />
        </Field>
        <div className="min-w-0 sm:col-span-2">
          <Field label="Zona horaria" htmlFor="settings-timezone">
            <Select value={tz} onValueChange={setTz}>
              <SelectTrigger id="settings-timezone" className="h-11 w-full [&>span]:truncate" data-testid="settings-timezone-select"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72 bg-white">
                <SelectGroup>
                  <SelectLabel>México</SelectLabel>
                  {MX_TIMEZONES.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`timezone-option-${t.value}`}>{t.label}</SelectItem>)}
                </SelectGroup>
                <SelectGroup>
                  <SelectLabel>Otras zonas</SelectLabel>
                  {OTHERS.map((z) => <SelectItem key={z} value={z}>{z.replace(/_/g, " ")}</SelectItem>)}
                </SelectGroup>
              </SelectContent>
            </Select>
          </Field>
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={!dirty || !name.trim() || saving} className="h-11 bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="settings-profile-save-button">
          <Save className="mr-2 h-4 w-4" /> {saving ? "Guardando…" : "Guardar cambios"}
        </Button>
      </div>
    </SettingsSection>
  );
}
